import { Button, Flex, Typography } from "antd";
import {
  ArrowRightOutlined,
  DollarOutlined,
  TruckOutlined,
} from "@ant-design/icons";
import React from "react";
import { useTranslation } from "react-i18next";

import {
  FacturaAprobadaIcon,
  PagoCanceladoIcon,
  PagoEnRevisionIcon,
  PagoPendienteIcon,
  PagoVerificadoIcon,
} from "../icon/PagoPendienteIcon";
import {
  EstadoPaso,
  FlujoPedido,
  PasoPedido,
  useEsperaDelAvance,
} from "../order-flow/flujo-pedido";

type OrderStepsProps = {
  paymentId?: string | number;
  currentStep: number;
  stepStatus: any;
  canSendReview: boolean;
  uploadInvoice: boolean;
  canSendInvoice: boolean;
  handleChangeStep?: (status: string, extraPayload?: any) => void;
  loadingStep: boolean;
  delivery?: any;
  paymentStatus: string;
  cantInitPayment: boolean;
  selectedPayment: any;
};

const { Text } = Typography;

export const OrderSteps: React.FC<OrderStepsProps> = ({
  paymentId,
  currentStep,
  canSendInvoice,
  handleChangeStep,
  loadingStep = false,
  delivery,
  paymentStatus,
  cantInitPayment = false,
  selectedPayment,
}) => {
  const { t } = useTranslation();
  const { esperando, esperar } = useEsperaDelAvance(currentStep);

  const isCanceled = paymentStatus === "canceled";

  const isOnlinePayment =
    selectedPayment !== "firstdata" && selectedPayment !== "mercadopago_basic";

  // El último paso, Facturado, queda hecho al llegar: no tiene nada más que hacer.
  const estado = (i: number, ultimo = false): EstadoPaso =>
    currentStep > i || (ultimo && currentStep >= i)
      ? "hecho"
      : currentStep === i
      ? "actual"
      : "pendiente";

  const pasos: PasoPedido[] = isCanceled
    ? [
        {
          key: "pending",
          icon: <PagoPendienteIcon />,
          label: t("orders.steps.pendingPayment"),
          estado: "error",
        },
        {
          key: "canceled",
          icon: <PagoCanceladoIcon />,
          label: t("orders.steps.canceled"),
          estado: "error",
        },
      ]
    : [
        {
          key: "pending",
          icon: <PagoPendienteIcon />,
          label: t("orders.steps.pendingPayment"),
          estado: estado(0),
        },
        {
          key: "review",
          icon: <PagoEnRevisionIcon />,
          label: t("orders.steps.paymentReview"),
          estado: estado(1),
        },
        {
          key: "verified",
          icon: <PagoVerificadoIcon />,
          label: t("orders.steps.paymentVerified"),
          estado: estado(2),
        },
        {
          key: "invoiced",
          icon: <FacturaAprobadaIcon />,
          label: t("orders.steps.invoicedApproved"),
          estado: estado(3, true),
        },
      ];

  const botonAvanzar = (texto: string, siguiente: string, disabled = false) => (
    <Button
      loading={loadingStep || esperando}
      type="primary"
      disabled={disabled}
      iconPosition="end"
      icon={<ArrowRightOutlined />}
      onClick={() => {
        esperar();
        handleChangeStep?.(siguiente);
      }}
    >
      {texto}
    </Button>
  );

  const boton = isCanceled
    ? undefined
    : currentStep === 0 && isOnlinePayment
    ? {
        despuesDe: "pending",
        nodo: botonAvanzar(
          t("orders.steps.sendForReview"),
          "review",
          !cantInitPayment,
        ),
      }
    : currentStep === 1
    ? {
        despuesDe: "review",
        nodo: botonAvanzar(t("orders.steps.confirmPayment"), "verified"),
      }
    : currentStep === 2
    ? {
        despuesDe: "verified",
        nodo: botonAvanzar(
          t("orders.steps.invoiceAndSend"),
          "invoiced",
          !canSendInvoice,
        ),
      }
    : undefined;

  // Las entregas del pago, al final: cada nombre baja hasta 3 renglones y, si no alcanza,
  // se corta con "…" y el completo aparece al pasar el mouse.
  const entregas = delivery?.filter(Boolean)?.length ? (
    delivery.filter(Boolean).map((item: any) => (
      <Flex
        align="flex-start"
        gap={6}
        className="flujo-pedido__entrega"
        key={item.shipmentId}
      >
        <TruckOutlined style={{ fontSize: 20 }} />
        <Button
          type="link"
          className="flujo-pedido__entrega-nombre"
          title={item.name}
          onClick={() => {
            window.dispatchEvent(
              new CustomEvent("openShipmentPanel", {
                detail: { shipmentId: item.shipmentId },
              }),
            );
            setTimeout(() => {
              document
                .getElementById(`shipment-panel-${item.shipmentId}`)
                ?.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 350);
          }}
        >
          <Text type="secondary">{item.name}</Text>
        </Button>
      </Flex>
    ))
  ) : (
    <Text type="secondary" style={{ fontSize: 12 }}>
      {t("orders.steps.orderInvoice")}
    </Text>
  );

  return (
    <FlujoPedido
      id={paymentId !== undefined ? `pago-${paymentId}` : undefined}
      pasos={pasos}
      boton={boton}
      // Un pago cancelado no llegó a facturarse: el flujo termina en Cancelado, sin la
      // factura ni las entregas (Diseño, 2026-10-19).
      final={isCanceled ? undefined : entregas}
    />
  );
};
